/**
 * MergeDetailDialog.jsx — AP4 Feld-für-Feld-Merge
 *
 * Öffnet sich wenn der Superadmin "Zusammenführen" klickt.
 * Zeigt für jedes relevante Feld (Name, EAN, Art.-Nr., Beschreibung, Bild, PDF)
 * beide Seiten nebeneinander mit Radio-Auswahl, welcher Wert übernommen wird.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle2, XCircle, GitMerge, Loader2, ExternalLink } from 'lucide-react';

// Ein einzelnes Vergleichs-Feld
function FieldRow({ label, existingVal, newVal, fieldKey, choice, onChange }) {
  const hasExisting = !!existingVal;
  const hasNew      = !!newVal;
  const different   = existingVal !== newVal;

  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid={`merge-field-${fieldKey}`}>
      <div className="bg-muted/30 px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {label}
        {!different && hasExisting && (
          <span className="ml-2 text-green-600 font-normal normal-case">identisch</span>
        )}
      </div>
      <div className="grid grid-cols-2 divide-x divide-border">
        {/* Bestehend */}
        <label className={`flex items-start gap-2 p-3 cursor-pointer transition-colors ${
          choice === 'existing' ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-muted/20'
        }`}>
          <input
            type="radio" name={fieldKey} value="existing"
            checked={choice === 'existing'}
            onChange={() => onChange(fieldKey, 'existing')}
            className="mt-0.5 shrink-0 accent-blue-600"
          />
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wide mb-0.5">Bestehend</p>
            {hasExisting
              ? <p className="text-sm break-words">{existingVal}</p>
              : <p className="text-xs text-muted-foreground italic flex items-center gap-1"><XCircle className="h-3 w-3 text-amber-500" />kein Wert</p>
            }
          </div>
        </label>

        {/* Neu */}
        <label className={`flex items-start gap-2 p-3 cursor-pointer transition-colors ${
          choice === 'new' ? 'bg-primary/5 dark:bg-primary/10' : 'hover:bg-muted/20'
        }`}>
          <input
            type="radio" name={fieldKey} value="new"
            checked={choice === 'new'}
            onChange={() => onChange(fieldKey, 'new')}
            className="mt-0.5 shrink-0 accent-primary"
          />
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-primary uppercase tracking-wide mb-0.5">Neu eingereicht</p>
            {hasNew
              ? <p className="text-sm break-words">{newVal}</p>
              : <p className="text-xs text-muted-foreground italic flex items-center gap-1"><XCircle className="h-3 w-3 text-amber-500" />kein Wert</p>
            }
          </div>
        </label>
      </div>
    </div>
  );
}

// Asset-Feld (Bild / PDF) — zeigt ✓/✗ + Radio
function AssetRow({ label, fieldKey, existingUrl, newHas, choice, onChange }) {
  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid={`merge-field-${fieldKey}`}>
      <div className="bg-muted/30 px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className="grid grid-cols-2 divide-x divide-border">
        <label className={`flex items-center gap-2 p-3 cursor-pointer transition-colors ${
          choice === 'existing' ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-muted/20'
        }`}>
          <input type="radio" name={fieldKey} value="existing"
            checked={choice === 'existing'} onChange={() => onChange(fieldKey, 'existing')}
            className="shrink-0 accent-blue-600" />
          <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wide">Bestehend</p>
          {existingUrl
            ? <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto" />
            : <XCircle className="h-4 w-4 text-amber-500 ml-auto" />}
        </label>
        <label className={`flex items-center gap-2 p-3 cursor-pointer transition-colors ${
          choice === 'new' ? 'bg-primary/5 dark:bg-primary/10' : 'hover:bg-muted/20'
        }`}>
          <input type="radio" name={fieldKey} value="new"
            checked={choice === 'new'} onChange={() => onChange(fieldKey, 'new')}
            className="shrink-0 accent-primary" />
          <p className="text-[10px] font-bold text-primary uppercase tracking-wide">Neu eingereicht</p>
          {newHas
            ? <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto" />
            : <XCircle className="h-4 w-4 text-amber-500 ml-auto" />}
        </label>
      </div>
    </div>
  );
}


export function MergeDetailDialog({ item, onConfirm, onCancel, loading }) {
  const incoming  = item?.incoming_submission || {};
  const existing  = item?.existing_masters?.[0] || {};

  // Initialwahl: Bestehenden Wert nehmen wenn vorhanden, sonst neuen
  const defaultChoices = {
    product_name:   existing.product_name ? 'existing' : 'new',
    ean:            existing.ean            ? 'existing' : 'new',
    article_number: existing.article_number ? 'existing' : 'new',
    description:    existing.description   ? 'existing' : 'new',
    image:          existing.has_image      ? 'existing' : 'new',
    pdf:            existing.has_pdf        ? 'existing' : 'new',
  };

  const [choices, setChoices] = useState(defaultChoices);
  useEffect(() => { setChoices(defaultChoices); }, [item?.id]); // eslint-disable-line

  if (!item) return null;

  const handleChange = (field, val) => setChoices(prev => ({ ...prev, [field]: val }));

  const handleSave = () => {
    const merged_fields = {
      product_name:   choices.product_name   === 'new' ? (incoming.product_name || existing.product_name) : existing.product_name,
      ean:            choices.ean            === 'new' ? (incoming.ean || existing.ean)                   : existing.ean,
      article_number: choices.article_number === 'new' ? (incoming.article_number || existing.article_number) : existing.article_number,
      description:    choices.description   === 'new' ? '' : (existing.description || ''),
    };
    onConfirm({
      action:           'merge',
      merged_fields,
      target_master_id: existing.id,
    });
  };

  return (
    <Dialog open={!!item} onOpenChange={v => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="merge-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-primary" />
            Zusammenführen — Felder auswählen
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground mb-1">
          Wähle für jedes Feld, welcher Wert ins Ergebnis übernommen werden soll.
        </p>

        <div className="space-y-3">
          <FieldRow
            label="Produktname"
            fieldKey="product_name"
            existingVal={existing.product_name}
            newVal={incoming.product_name}
            choice={choices.product_name}
            onChange={handleChange}
          />
          <FieldRow
            label="EAN"
            fieldKey="ean"
            existingVal={existing.ean}
            newVal={incoming.ean}
            choice={choices.ean}
            onChange={handleChange}
          />
          <FieldRow
            label="Artikelnummer"
            fieldKey="article_number"
            existingVal={existing.article_number}
            newVal={incoming.article_number}
            choice={choices.article_number}
            onChange={handleChange}
          />
          <FieldRow
            label="Beschreibung"
            fieldKey="description"
            existingVal={existing.description}
            newVal=""
            choice={choices.description}
            onChange={handleChange}
          />
          <AssetRow
            label="Bild"
            fieldKey="image"
            existingUrl={existing.image_url}
            newHas={false}
            choice={choices.image}
            onChange={handleChange}
          />
          <AssetRow
            label="PDF / Rezept"
            fieldKey="pdf"
            existingUrl={existing.pdf_url}
            newHas={false}
            choice={choices.pdf}
            onChange={handleChange}
          />
        </div>

        {/* Vorschau des Ergebnisses */}
        <div className="mt-2 bg-muted/30 rounded-xl p-3 space-y-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ergebnis-Vorschau</p>
          <p className="text-sm font-medium">
            {choices.product_name === 'new' ? (incoming.product_name || existing.product_name) : existing.product_name}
          </p>
          <div className="flex gap-2 flex-wrap">
            {(choices.ean === 'new' ? incoming.ean : existing.ean) && (
              <Badge variant="outline" className="text-xs">EAN: {choices.ean === 'new' ? incoming.ean : existing.ean}</Badge>
            )}
            {(choices.article_number === 'new' ? incoming.article_number : existing.article_number) && (
              <Badge variant="outline" className="text-xs">Art.-Nr.: {choices.article_number === 'new' ? incoming.article_number : existing.article_number}</Badge>
            )}
            {choices.image === 'existing' && existing.has_image && (
              <Badge variant="outline" className="text-xs text-green-700">Bild ✓</Badge>
            )}
            {choices.pdf === 'existing' && existing.has_pdf && (
              <Badge variant="outline" className="text-xs text-green-700">PDF ✓</Badge>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={loading} data-testid="merge-confirm-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <GitMerge className="h-4 w-4 mr-2" />}
            Zusammenführen &amp; speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
